<template>
  <b-modal hide-footer hide-header lazy centered :id="prefixId + id" size="lg">
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header modal-header-redesign pb-0">
          <p class="h5 modal-title modal-title-redesign">{{ translate('rdRateDetails') }}</p>
          <button type="button" class="close cross-margin" @click="hideModal" aria-label="Close">
            <span class="cross-color"></span>
            <span class="cross-color"></span>
          </button>
        </div>
        <div class="modal-body modal-body--text modal-body-redesign">
          <div v-if="stay" class="modal-container">
            <hr class="mb-4 separator mt-0" />

            <template v-if="stay.conditions.features.length">
              <p class="title-condition text-16 font-bold mt-3 mb-3 text-capitalize">
                <i class="icon-info-2 icon-rate-details sm"></i>
                {{ translate('boNewFeatures') }}
              </p>
              <ul class="text-14 list-style-none">
                <li :key="index" v-for="(feature, index) in stay.conditions.features">
                  {{ feature }}
                </li>
              </ul>
            </template>

            <template>
              <p class="title-condition text-16 font-bold mt-4 mb-3 text-capitalize">
                <i class="icon-early-check-in-and-late-check-out icon-rate-details sm"></i>
                {{ translate('boCheckIn') }} {{ translate('boAnd') }} {{ translate('boCheckOut') }}
              </p>
              <ul class="text-14 list-style-none">
                <li>
                  {{ translate('boCheckIn') }}: {{ checkInTime }} h | {{ translate('boCheckOut') }}:
                  {{ checkOutTime }} h
                </li>
                <li>{{ translate('boCheckInCheckOutAdvice') }}</li>
              </ul>
            </template>

            <template>
              <p class="title-condition text-16 font-bold mt-4 mb-0 text-capitalize">
                <i class="icon-best-price-guaranteed icon-rate-details sm"></i>
                {{ translate('boPricesPerDay') }}
              </p>
              <div>
                <ol class="timeline-container">
                  <li class="timeline-item" v-for="(night, index) in stay.nights" :key="index">
                    <div class="rate d-flex flex-column pl-2 pr-4 mb-1">
                      <p v-if="night.rate && night.rate.type === 'saver'" class="saver-label">
                        {{ translate('boSaverRates') }}
                      </p>
                      <div class="rate__price">
                        <span class="rate__day text-14">
                          {{ night.date | toDate }}
                        </span>
                        <strong v-if="night.pricing.price" class="rate__price-night">
                          {{
                            exchangeRates.selectedOption
                              ? applyExchangeRate(night.pricing.price.amount)
                              : night.pricing.price.formattedAmount
                          }}
                        </strong>
                        <strong
                          v-if="
                            night.pricing.points && night.pricing.points != 0 && night.pricing.price
                          "
                        >
                          +
                        </strong>
                        <strong
                          v-if="night.pricing.points && night.pricing.points != 0"
                          class="points"
                          >{{
                            translate('boNumPoints', night.pricing.points, night.pricing.points)
                          }}</strong
                        >
                        <span class="color-per-day text-12">
                          {{
                            night.pricing.price ? translate('chPerNight') : translate('boFreeNight')
                          }}</span
                        >
                      </div>
                    </div>
                  </li>
                </ol>
              </div>
              <div class="taxes mt-3">
                <span
                  class="color-per-day text-12"
                  v-if="
                    stay.averagePricing &&
                    !stay.averagePricing.taxIncluded &&
                    stay.totalPriceWithTax
                  "
                  >{{
                    translate(
                      'boExcludedTaxesAndFees',
                      '{0}',
                      exchangeRates.selectedOption
                        ? applyExchangeRate(stay.totalPriceWithTax.totalTaxAndFee)
                        : stay.totalPriceWithTax.formattedTotalTaxAndFee
                    )
                  }}</span
                >
                <span
                  class="color-per-day text-12"
                  v-if="stay.averagePricing && stay.averagePricing.taxIncluded"
                >
                  {{ translate('rdUKTaxIncluded') }}</span
                >
              </div>
            </template>

            <template
              v-if="
                stay.conditions.cancellationPoliciesExtended.policiesV2 &&
                stay.conditions.cancellationPoliciesExtended.policiesV2.length > 0
              "
            >
              <p class="title-condition text-16 font-bold mt-4 mb-3 text-capitalize">
                <i class="icon-calendar-no-availability icon-rate-details sm"></i>
                {{ translate('rdCancellationPolicy') }}
              </p>
              <ul class="text-14 list-style-none">
                <div
                  :key="index"
                  v-for="(policy, index) in stay.conditions.cancellationPoliciesExtended.policiesV2"
                >
                  <li
                    v-for="subPolicy in policy.text"
                    :key="subPolicy"
                    v-html="replaceValueCancellationPolicy(subPolicy, policy.amountAfterTax)"
                  ></li>
                </div>
              </ul>
            </template>

            <template v-else-if="stay.conditions.cancellationPoliciesExtended.policies.length">
              <p class="title-condition text-16 font-bold mt-4 mb-3 text-capitalize">
                <i class="icon-calendar-no-availability icon-rate-details sm"></i>
                {{ translate('rdCancellationPolicy') }}
              </p>
              <ul class="text-14 list-style-none">
                <li
                  :key="index"
                  v-for="(policy, index) in stay.conditions.cancellationPoliciesExtended.policies"
                >
                  {{ policy }}
                </li>
              </ul>
            </template>

            <template v-if="stay.conditions.guaranteePolicies.length">
              <p class="title-condition text-16 font-bold mt-4 mb-3 text-capitalize">
                <i class="icon-room-availability-guarantee icon-rate-details sm"></i>
                {{ translate('rdGuaranteePolicy') }}
              </p>
              <ul class="text-14 list-style-none">
                <li :key="index" v-for="(policy, index) in stay.conditions.guaranteePolicies">
                  {{ policy }}
                </li>
              </ul>
            </template>

            <template v-if="stay.conditions.instructions.length">
              <p class="title-condition text-16 font-bold mt-4 mb-3 text-capitalize">
                <i class="icon-friends icon-rate-details sm"></i>
                {{ translate('boRRPolicy') }}
              </p>
              <ul class="text-14 list-style-none">
                <li :key="index" v-for="(instruction, index) in stay.conditions.instructions">
                  {{ instruction }}
                </li>
              </ul>
            </template>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import { getQueryParams } from '../../../infrastructure/Utils'

const { mapGetters } = createNamespacedHelpers('multiRoomDisplay')
// TODO this module is legacy copy-pasted from old version (temporal patch)
export default {
  name: 'StayDetailsModal',
  props: {
    stay: Object,
    prefixId: {
      type: String,
      default: 'stayDetails-'
    },
    id: String,
    checkInTime: String,
    checkOutTime: String,
    undiscountedStay: Boolean,
    isEcert: {
      type: Boolean,
      default: false
    },
    hasTaxIncluded: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters(['exchangeRates']),
    isUndiscountedAmountVisible() {
      const ecertAppliedOrNotInTaxIncludedRegion = this.isEcert || !this.hasTaxIncluded
      const discountApplied = !this.undiscountedStay
      const price = !!this.night && !!this.night.pricing && this.night.pricing.price
      const bothPriceToShow = price && price.undiscountedAmount

      return ecertAppliedOrNotInTaxIncludedRegion && discountApplied && bothPriceToShow
    },
    showStayDescription() {
      return !!this.stay.nights.find(night => night.rate && night.rate.type === 'saver')
    }
  },
  methods: {
    checkInDate() {
      const { checkInDate } = getQueryParams(window.location._search || window.location.search)
      return checkInDate
    },
    checkOutDate() {
      const { checkOutDate } = getQueryParams(window.location._search || window.location.search)
      return checkOutDate
    },
    applyExchangeRate(amount) {
      return (
        this.exchangeRates.selectedOption.code +
        ' ' +
        parseFloat(amount * this.exchangeRates.selectedOption.exchangeRate).toFixed(2)
      )
    },
    hideModal() {
      this.$bvModal.hide(this.prefixId + this.id)
    },
    replaceValueCancellationPolicy(policy, amountAfterTax) {
      return policy.replaceAll(
        '<value_amount>',
        `<span class="color-primary">${this.applyExchangeRate(amountAfterTax)}</span>`
      )
    }
  }
}
</script>
<style lang="scss" scoped>
html[dir='rtl'] {
  .modal-container {
    text-align: right;
  }
  .modal-title-redesign {
    margin-right: 4rem;
    margin-left: 0rem;
    font-size: 1.724rem;
    @media (max-width: 772px) {
      margin-right: 2rem !important;
      font-size: 1.714rem;
    }
  }
  .cross-margin {
    margin-left: 4rem;
    @media (max-width: 772px) {
      margin-left: 2rem !important;
    }
  }
}
</style>
